import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/ui/Button';
import workerProfileService from '../../services/workerProfileService';
import serviceAreaContentService from '../../services/serviceAreaContentService';
import { useAuth } from '../../contexts/AuthContext';

const JobSectorBrowserSolarPanelServices = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('alle');
  const [selectedInstallationType, setSelectedInstallationType] = useState('all');
  const [locationRadius, setLocationRadius] = useState('50');
  const [certifiedInstallers, setCertifiedInstallers] = useState([]);
  const [loadingInstallers, setLoadingInstallers] = useState(true);
  const [news, setNews] = useState([]);
  const [areaContent, setAreaContent] = useState([]);
  const [loadingContent, setLoadingContent] = useState(true);

  useEffect(() => {
    loadWorkerProfiles();
    loadNewsAndContent();
  }, []);

  const loadWorkerProfiles = async () => {
    try {
      setLoadingInstallers(true);
      const data = await workerProfileService?.getWorkerProfilesByServiceArea('Zonnepanelen');
      setCertifiedInstallers(data || []);
    } catch (error) {
      console.error('Error loading worker profiles:', error);
    } finally {
      setLoadingInstallers(false);
    }
  };

  const loadNewsAndContent = async () => {
    try {
      setLoadingContent(true);
      const [newsData, contentData] = await Promise.all([
        serviceAreaContentService?.getNewsByServiceArea('Zonnepanelen'),
        serviceAreaContentService?.getContentByServiceArea('Zonnepanelen')
      ]);
      setNews(newsData || []);
      setAreaContent(contentData || []);
    } catch (error) {
      console.error('Error loading news and content:', error);
    } finally {
      setLoadingContent(false);
    }
  };

  const handleContactClick = (installer) => {
    if (!user) {
      // Redirect to login if not authenticated
      navigate('/login', { state: { from: window.location?.pathname } });
      return;
    }
    // Handle contact logic here (e.g., open modal, send message)
    console.log('Contact installer:', installer);
  };

  const solarJobs = [
    {
      title: 'Residentiële zonnepanelen installatie',
      location: 'Amsterdam',
      budget: '€8.000 - €12.000',
      deadline: '4 weken',
      description: 'Installatie 16 zonnepanelen op schuin dak met omvormer en monitoring',
      installationType: 'Residentieel',
      capacity: '6.4 kWp',
      panelType: 'Monokristallijn',
      roofType: 'Schuin dak - pannen',
      certification: 'NEN 3140, VCA',
      hourlyRate: '€70',
      complexity: 'medium'
    },
    {
      title: 'Commerciële zonnepanelen project',
      location: 'Rotterdam',
      budget: '€45.000 - €65.000',
      deadline: '8 weken',
      description: 'Grootschalige installatie 150 panelen op bedrijfspand met 3-fase omvormers',
      installationType: 'Commercieel',
      capacity: '60 kWp',
      panelType: 'Bifacial',
      roofType: 'Plat dak - ballast',
      certification: 'NEN 3140, VCA, ISO 9001',
      hourlyRate: '€85',
      complexity: 'high'
    },
    {
      title: 'Zonnepanelen met batterij opslag',
      location: 'Utrecht',
      budget: '€15.000 - €22.000',
      deadline: '5 weken',
      description: 'Zonnepanelen installatie met thuisbatterij en slimme energie management',
      installationType: 'Residentieel + Opslag',
      capacity: '8.5 kWp + 10 kWh',
      panelType: 'Monokristallijn',
      roofType: 'Schuin dak - pannen',
      certification: 'NEN 3140, VCA, Battery certified',
      hourlyRate: '€75',
      complexity: 'high'
    },
    {
      title: 'Onderhoud zonnepanelen systeem',
      location: 'Den Haag',
      budget: '€600 - €1.200',
      deadline: '1 week',
      description: 'Jaarlijks onderhoud en inspectie bestaand zonnepanelen systeem',
      installationType: 'Onderhoud',
      capacity: '5.2 kWp',
      panelType: 'Polykristallijn',
      roofType: 'Schuin dak',
      certification: 'NEN 3140',
      hourlyRate: '€65',
      complexity: 'low'
    },
    {
      title: 'Industriële zonnepanelen installatie',
      location: 'Eindhoven',
      budget: '€120.000 - €180.000',
      deadline: '12 weken',
      description: 'Grootschalig industrieel project 400+ panelen met monitoring systeem',
      installationType: 'Industrieel',
      capacity: '160 kWp',
      panelType: 'Bifacial high-efficiency',
      roofType: 'Plat dak - geballast systeem',
      certification: 'NEN 3140, VCA, ATEX',
      hourlyRate: '€90',
      complexity: 'high'
    },
    {
      title: 'Zonnepanelen op carport',
      location: 'Groningen',
      budget: '€12.000 - €18.000',
      deadline: '6 weken',
      description: 'Zonnepanelen carport constructie met laadpaal voor elektrische auto',
      installationType: 'Carport + Laadpaal',
      capacity: '7.2 kWp',
      panelType: 'Monokristallijn',
      roofType: 'Carport constructie',
      certification: 'NEN 3140, VCA, Laadpaal certified',
      hourlyRate: '€78',
      complexity: 'medium'
    }
  ];

  const installationTypes = [
    { value: 'all', label: 'Alle installaties' },
    { value: 'residential', label: 'Residentieel' },
    { value: 'commercial', label: 'Commercieel' },
    { value: 'industrial', label: 'Industrieel' },
    { value: 'storage', label: 'Met batterij opslag' },
    { value: 'carport', label: 'Carport systemen' },
    { value: 'maintenance', label: 'Onderhoud' }
  ];

  const getComplexityColor = (complexity) => {
    const colors = {
      high: 'bg-error/10 text-error border-error/20',
      medium: 'bg-warning/10 text-warning border-warning/20',
      low: 'bg-success/10 text-success border-success/20'
    };
    return colors?.[complexity] || colors?.low;
  };

  const getComplexityLabel = (complexity) => {
    const labels = {
      high: 'Complex',
      medium: 'Gemiddeld',
      low: 'Basis'
    };
    return labels?.[complexity] || 'Basis';
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#0A0A0A]">
        {/* Hero Section */}
        <motion.section 
          className="py-16 md:py-24"
          style={{ background: '#0A0A0A', borderBottom: '1px solid #1A1A1A' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <Link to="/" className="inline-flex items-center gap-2 text-sm mb-6 transition-colors hover:text-white" style={{ color: '#888888' }}>
                <Icon name="ArrowLeft" size={16} />
                Terug naar home
              </Link>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,229,160,0.1)', border: '1px solid rgba(0,229,160,0.2)' }}>
                  <Icon name="Sun" size={32} color="#00E5A0" />
                </div>
                <h1 className="text-4xl md:text-5xl font-heading font-bold text-white">
                  Zonnepanelen Installaties - Vakgebied Browser
                </h1>
              </div>
              <p className="text-lg max-w-3xl" style={{ color: '#888888' }}>
                Gespecialiseerde zonnepanelen opdrachten en gecertificeerde installateurs voor residentiële, commerciële en industriële projecten.
              </p>
            </motion.div>
          </div>
        </motion.section>

        {/* Stats Section */}
        <section className="py-12 border-b border-border">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: 'Actieve opdrachten', value: '72', icon: 'Briefcase' },
                { label: 'Gecertificeerde installateurs', value: '289', icon: 'Users' },
                { label: 'Gemiddelde rating', value: '4.9', icon: 'Star' },
                { label: 'Voltooide projecten', value: '4.8k+', icon: 'CheckCircle' }
              ]?.map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Icon name={stat?.icon} size={24} className="text-primary mx-auto mb-2" />
                  <div className="text-3xl font-bold text-foreground mb-1">{stat?.value}</div>
                  <div className="text-sm text-muted-foreground">{stat?.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="py-8 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="bg-white border-2 border-slate-200 rounded-xl p-6" style={{
              boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
            }}>
              <h3 className="font-heading font-semibold text-lg text-foreground mb-4">Geavanceerde Filters</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Installatie Type</label>
                  <select 
                    value={selectedInstallationType}
                    onChange={(e) => setSelectedInstallationType(e?.target?.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {installationTypes?.map(type => (
                      <option key={type?.value} value={type?.value}>{type?.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Locatie Radius</label>
                  <select 
                    value={locationRadius}
                    onChange={(e) => setLocationRadius(e?.target?.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="10">Binnen 10 km</option>
                    <option value="25">Binnen 25 km</option>
                    <option value="50">Binnen 50 km</option>
                    <option value="100">Binnen 100 km</option>
                    <option value="all">Heel Nederland</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Complexiteit</label>
                  <select 
                    value={activeFilter}
                    onChange={(e) => setActiveFilter(e?.target?.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="alle">Alle projecten</option>
                    <option value="low">Basis</option>
                    <option value="medium">Gemiddeld</option>
                    <option value="high">Complex</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Jobs Section */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-heading font-bold text-foreground">Beschikbare Opdrachten</h2>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Briefcase" size={16} />
                <span>{solarJobs?.length} opdrachten gevonden</span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {solarJobs?.map((job, index) => (
                <motion.div
                  key={index}
                  className="bg-white border-2 border-slate-200 rounded-xl p-6 hover:border-primary transition-all"
                  style={{
                    boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{job?.title}</h3>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <Icon name="MapPin" size={14} />
                        <span>{job?.location}</span>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getComplexityColor(job?.complexity)}`}>
                      {getComplexityLabel(job?.complexity)}
                    </span>
                  </div>

                  <p className="text-sm text-muted-foreground mb-4">{job?.description}</p>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Zap" size={14} className="text-primary" />
                      <span className="text-muted-foreground">{job?.capacity}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Package" size={14} className="text-primary" />
                      <span className="text-muted-foreground">{job?.panelType}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Home" size={14} className="text-primary" />
                      <span className="text-muted-foreground">{job?.roofType}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Award" size={14} className="text-primary" />
                      <span className="text-muted-foreground">{job?.certification}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div>
                      <div className="text-2xl font-bold text-foreground">{job?.budget}</div>
                      <div className="text-xs text-muted-foreground">{job?.hourlyRate}/uur</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <div className="text-sm font-medium text-foreground">Deadline</div>
                        <div className="text-xs text-muted-foreground">{job?.deadline}</div>
                      </div>
                      <Button size="sm" className="ml-2">
                        Direct Solliciteren
                        <Icon name="ArrowRight" size={14} />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Certified Installers Section */}
        <section className="py-12 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-heading font-bold text-foreground">Gecertificeerde Installateurs</h2>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Users" size={16} />
                <span>{certifiedInstallers?.length} installateurs beschikbaar</span>
              </div>
            </div>

            {loadingInstallers ? (
              <div className="text-center py-8 text-muted-foreground">Laden...</div>
            ) : certifiedInstallers?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">Geen installateurs beschikbaar</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {certifiedInstallers?.map((installer, index) => (
                  <motion.div
                    key={installer?.id || index}
                    className="bg-white border-2 border-slate-200 rounded-xl p-6"
                    style={{
                      boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{installer?.name}</h3>
                        <div className="flex items-center gap-3 mb-2">
                          <div className="flex items-center gap-1">
                            <Icon name="Star" size={14} className="text-warning fill-warning" />
                            <span className="text-sm font-medium text-foreground">{installer?.rating}</span>
                            <span className="text-xs text-muted-foreground">({installer?.reviews} reviews)</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Icon name="MapPin" size={14} />
                          <span>{installer?.location}</span>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="text-sm font-medium text-foreground mb-2">Specialisatie</div>
                      <div className="text-sm text-primary font-medium">{installer?.specialization}</div>
                    </div>

                    <div className="mb-4">
                      <div className="text-sm font-medium text-foreground mb-2">Certificeringen</div>
                      <div className="flex flex-wrap gap-2">
                        {installer?.certifications?.map((cert, idx) => (
                          <span key={idx} className="px-2 py-1 bg-success/10 text-success text-xs rounded-full border border-success/20">
                            {cert}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="text-sm font-medium text-foreground mb-2">Garantie</div>
                      <div className="text-sm text-muted-foreground">{installer?.warranty}</div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Ervaring</div>
                        <div className="text-sm font-medium text-foreground">{installer?.experience}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Voltooide projecten</div>
                        <div className="text-sm font-medium text-foreground">{installer?.completed_projects}+</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Uurtarief</div>
                        <div className="text-xl font-bold text-foreground">{installer?.hourly_rate}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground mb-1">Beschikbaarheid</div>
                          <div className="text-sm font-medium text-success">{installer?.availability}</div>
                        </div>
                        <Button size="sm" variant="outline" className="ml-2">
                          <Icon name="Phone" size={14} />
                          Contacteren
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Area Content Section */}
        {!loadingContent && areaContent?.length > 0 && (
          <section className="py-12">
            <div className="max-w-7xl mx-auto px-4 lg:px-6">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-8">Waarom kiezen voor onze zonnepanelen specialisten?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {areaContent?.map((content, index) => (
                  <motion.div
                    key={content?.id}
                    className="bg-white border-2 border-slate-200 rounded-xl p-6"
                    style={{
                      boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="CheckCircle" size={24} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{content?.title}</h3>
                        <p className="text-sm text-muted-foreground">{content?.content}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Latest News Section */}
        {!loadingContent && news?.length > 0 && (
          <section className="py-12 bg-muted/30">
            <div className="max-w-7xl mx-auto px-4 lg:px-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-heading font-bold text-foreground">Laatste Nieuws</h2>
                <Icon name="Newspaper" size={24} className="text-primary" />
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {news?.map((newsItem, index) => (
                  <motion.div
                    key={newsItem?.id}
                    className="bg-white border-2 border-slate-200 rounded-xl p-6"
                    style={{
                      boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                      <Icon name="Calendar" size={14} />
                      <span>{new Date(newsItem?.published_date)?.toLocaleDateString('nl-NL')}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-3">{newsItem?.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{newsItem?.content}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="User" size={14} />
                      <span>{newsItem?.author}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-primary to-primary-dark text-white">
          <div className="max-w-4xl mx-auto px-4 lg:px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Icon name="Sun" size={48} className="mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Klaar om te starten met zonne-energie?
              </h2>
              <p className="text-lg text-white/90 mb-8">
                Vind de perfecte zonnepanelen opdracht of laat uw expertise zien aan opdrachtgevers
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="outline" className="bg-white text-primary hover:bg-white/90">
                  <Icon name="Search" size={18} />
                  Bekijk alle opdrachten
                </Button>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  <Icon name="UserPlus" size={18} />
                  Registreer als installateur
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </MainLayout>
  );
};

export default JobSectorBrowserSolarPanelServices;